// -----------------------------------------------------------------------------
// Eyes — cartoon eyes whose pupils follow the cursor and blink now and then.
//
// Every `.eye` group in the SVG becomes its own Eye instance with its own aim.
// The EyeSet only broadcasts (target, blink, update) to all eyes; each eye
// decides for itself whether to follow. Because each eye aims from its own
// centre, the pupils converge on the cursor instead of pointing in parallel.
//
// The blink is an eyelid mask (a clip path per eye), so the drawing is hidden,
// never squashed. The lid glides between the animator's keyframes; set
// BLINK_FPS to 12 for the original stepped look. The eyes blink on their own
// and on any click.
//
// The pupil is masked by the white: it sits in a wrapper clipped to a copy of
// the white's path, so it slides behind the edge instead of spilling over it.
//
// `Eyes` drives a set: it tracks the cursor, runs the frame loop and can be
// started and stopped, so any component can own a pair. See
// components/berg-eyes.js (the character) and components/main-nav.js (the
// overlay's close button, which looks up while hovered).
//
// Markup: any root element > svg > `.eye` > `.eye__white` + `.eye__pupil`.
// No ids are used in the markup; the clip path ids are generated per eye.
//
// `lookAt(getPoint)` swaps the cursor for any other point until `lookAt(null)`,
// e.g. components/newsletter.js has the eyes follow the caret in its field.
//
// Console (debug): `eyes` is the character's set, `navEyes` the overlay's
// controller (so `navEyes.set`). Try `eyes.eyes[1].follow = false`,
// `eyes.blink()`, `eyes.eyes.forEach((eye) => (eye.inset = 0))`. Both handles
// are replaced whenever their component remounts, so re-read them after a page
// transition instead of keeping a variable.
// -----------------------------------------------------------------------------

// How far short of the white's edge the pupil stops. 0 = its edge runs flush
// with the white's edge, 1 = no travel at all, negative = it slides partly
// behind the white's edge (the white masks it).
const INSET = -0.2

// The animator's AE blink as keyframes, `t` in seconds. Each is the lid's edge
// at the eye's left side, centre and right side, as a fraction of the eye's
// height from the top (0 = top, 1 = bottom). Everything above the edge is
// hidden. The lid is eased between keys; after the last one the eye is open.
const OPEN = -0.5 // clear above the white, so the open eye shows in full
const BLINK_KEYS = [
	{ t: 0, left: OPEN, mid: OPEN, right: OPEN },
	{ t: 0.06, left: 0.73, mid: 0.84, right: 0.66 }, // closing: crescent
	{ t: 0.1, left: 1.5, mid: 1.5, right: 1.5 }, // shut
	{ t: 0.14, left: 1.5, mid: 1.5, right: 1.5 }, // held shut
	{ t: 0.2, left: 0.86, mid: 0.95, right: 0.72 }, // opening: thin sliver
	{ t: 0.28, left: OPEN, mid: OPEN, right: OPEN },
]
const BLINK_DURATION = BLINK_KEYS[BLINK_KEYS.length - 1].t
// null = smooth. A number samples the lid at that frame rate instead, e.g. 12
// for the stepped look of the AE comp.
const BLINK_FPS = null
const BLINK_INTERVAL = [4, 10] // seconds between automatic blinks

// How far above the eyes the cross-eyed look-up aims, in eye radii. Lower the
// number to cross the pupils harder, raise it to look straighter up.
const LOOK_UP_HEIGHT = 3

const SVG_NS = 'http://www.w3.org/2000/svg'
let clipCount = 0

const randomBetween = ([min, max]) => min + Math.random() * (max - min)

// The lid's edge `time` seconds into a blink, smoothstepped between keys.
const blinkEdge = (time) => {
	if (BLINK_FPS) time = Math.floor(time * BLINK_FPS) / BLINK_FPS

	const i = BLINK_KEYS.findIndex((key) => key.t > time)
	if (i <= 0) return BLINK_KEYS[i === 0 ? 0 : BLINK_KEYS.length - 1]

	const a = BLINK_KEYS[i - 1]
	const b = BLINK_KEYS[i]
	const u = (time - a.t) / (b.t - a.t)
	const s = u * u * (3 - 2 * u)
	const mix = (key) => a[key] + (b[key] - a[key]) * s

	return { left: mix('left'), mid: mix('mid'), right: mix('right') }
}

// Circle as path data. Not used by default (the drawn paths are kept), but any
// generator with this signature can be passed as `makeShape`, e.g. a wobble.
export const circleShape = (cx, cy, r) => `M${cx - r},${cy}a${r},${r} 0 1,0 ${r * 2},0a${r},${r} 0 1,0 ${-r * 2},0`

export class Eye {
	constructor(group, { cx, cy, R, pr, follow = true, ease = 0.18, inset = 0.25, makeShape = null }) {
		this.group = group
		this.elWhite = group.querySelector('.eye__white')
		this.elPupil = group.querySelector('.eye__pupil')

		this.cx = cx
		this.cy = cy
		this.R = R
		this.pr = pr
		this.ease = ease
		this.inset = inset
		this.makeShape = makeShape

		this.follow = follow

		this.aimX = 0
		this.aimY = 0
		this.offsetX = 0
		this.offsetY = 0

		this.blinkTime = null
		this.lidShown = false

		this.elLidPath = document.createElementNS(SVG_NS, 'path')
		this.elLid = this.createClip('eye-lid', this.elLidPath)

		// The pupil moves by its own transform, so the clip goes on a still wrapper
		// around it; on the pupil itself the mask would travel along.
		this.elIrisPath = this.elWhite.cloneNode()
		this.elIrisPath.removeAttribute('class')
		this.elIris = this.createClip('eye-white', this.elIrisPath)
		this.elPupilWrap = document.createElementNS(SVG_NS, 'g')
		this.elPupilWrap.setAttribute('clip-path', `url(#${this.elIris.id})`)
		this.elPupil.before(this.elPupilWrap)
		this.elPupilWrap.appendChild(this.elPupil)
	}

	// Measure centre and radii from the drawn paths.
	static fromGroup(group, options = {}) {
		const white = group.querySelector('.eye__white').getBBox()
		const pupil = group.querySelector('.eye__pupil').getBBox()

		return new Eye(group, {
			cx: white.x + white.width / 2,
			cy: white.y + white.height / 2,
			R: Math.min(white.width, white.height) / 2,
			pr: Math.max(pupil.width, pupil.height) / 2,
			...options,
		})
	}

	// A clip path with an id of its own, so eyes never share or collide on one.
	createClip(name, shape) {
		const svg = this.group.ownerSVGElement
		const defs = svg.querySelector('defs') || svg.insertBefore(document.createElementNS(SVG_NS, 'defs'), svg.firstChild)

		const clip = document.createElementNS(SVG_NS, 'clipPath')
		clip.id = `${name}-${++clipCount}`
		clip.appendChild(shape)
		defs.appendChild(clip)
		return clip
	}

	// Back to the markup as it was, so a remount starts clean.
	destroy() {
		this.group.removeAttribute('clip-path')
		this.elPupilWrap.replaceWith(this.elPupil)
		this.elLid.remove()
		this.elIris.remove()
	}

	// Only honoured while this eye follows; `follow = false` freezes it.
	setTarget(x, y) {
		if (!this.follow) return
		this.aimAt(x, y)
	}

	// Aim regardless of `follow` — for deliberate commands such as lookUp().
	aimAt(x, y) {
		let dx = x - this.cx
		let dy = y - this.cy

		// The pupil stays inside a circle around the eye centre. `inset` sizes it:
		// 0 puts the pupil's edge flush with the white's edge, 1 pins the pupil to
		// the centre, and a negative value lets it travel outside the white.
		const max = Math.max(0, (this.R - this.pr) * (1 - this.inset))
		const len = Math.hypot(dx, dy)

		if (len > max) {
			dx *= max / len
			dy *= max / len
		}

		this.aimX = dx
		this.aimY = dy
	}

	lookForward() {
		this.aimX = 0
		this.aimY = 0
	}

	// Recentre, but only when this eye still follows. `lookForward()` stays the
	// deliberate command: `eye.follow = false; eye.lookForward()` holds forward.
	relax() {
		if (this.follow) this.lookForward()
	}

	// Ignored while a blink is running, so quick clicks don't restart it half-shut.
	blink() {
		if (this.blinkTime !== null) return
		this.blinkTime = 0
	}

	update(dt) {
		// `ease` is the fraction covered per 60 Hz frame; scale it to real dt.
		const k = 1 - Math.pow(1 - this.ease, dt * 60)
		this.offsetX += (this.aimX - this.offsetX) * k
		this.offsetY += (this.aimY - this.offsetY) * k

		if (this.blinkTime !== null) {
			this.blinkTime += dt
			if (this.blinkTime >= BLINK_DURATION) {
				this.blinkTime = null
			}
		}

		this.render()
	}

	render() {
		const px = this.cx + this.offsetX
		const py = this.cy + this.offsetY

		if (this.makeShape) {
			const white = this.makeShape(this.cx, this.cy, this.R)
			this.elWhite.setAttribute('d', white)
			this.elIrisPath.setAttribute('d', white)
			this.elPupil.setAttribute('d', this.makeShape(px, py, this.pr))
		} else {
			this.elPupil.setAttribute('transform', `translate(${this.offsetX} ${this.offsetY})`)
		}

		// Rewrite the lid every frame while blinking; outside a blink, only once.
		if (this.blinkTime !== null) {
			this.elLidPath.setAttribute('d', this.lidShape(blinkEdge(this.blinkTime)))
			if (!this.lidShown) this.group.setAttribute('clip-path', `url(#${this.elLid.id})`)
			this.lidShown = true
		} else if (this.lidShown) {
			this.group.removeAttribute('clip-path')
			this.lidShown = false
		}
	}

	// The visible part of the eye: everything below the lid's edge. The edge is a
	// curve through the left tip, the centre and the right tip. The shape runs
	// past the sides and bottom so a pupil outside the white is covered too.
	lidShape({ left, mid, right }) {
		const { cx, cy, R } = this
		const top = cy - R
		const yL = top + left * 2 * R
		const yM = top + mid * 2 * R
		const yR = top + right * 2 * R
		const control = (4 * yM - yL - yR) / 2
		const x0 = cx - 2 * R
		const x1 = cx + 2 * R
		const bottom = cy + 2 * R

		return `M${x0},${yL}L${cx - R},${yL}Q${cx},${control} ${cx + R},${yR}L${x1},${yR}L${x1},${bottom}L${x0},${bottom}Z`
	}
}

export class EyeSet {
	constructor(eyes) {
		this.eyes = eyes
	}

	setTarget(x, y) {
		this.eyes.forEach((eye) => eye.setTarget(x, y))
	}

	lookForward() {
		this.eyes.forEach((eye) => eye.lookForward())
	}

	relax() {
		this.eyes.forEach((eye) => eye.relax())
	}

	// Look up, cross-eyed: every eye aims at one point above and between them,
	// so each one leans inward on its own. No per-eye maths needed.
	lookUp() {
		if (!this.eyes.length) return

		const x = this.eyes.reduce((sum, eye) => sum + eye.cx, 0) / this.eyes.length
		const y = this.eyes.reduce((sum, eye) => sum + eye.cy - eye.R * LOOK_UP_HEIGHT, 0) / this.eyes.length

		this.eyes.forEach((eye) => eye.aimAt(x, y))
	}

	// Look straight down: every eye aims below its own centre, so the pupils stay
	// parallel — the eyes follow something falling rather than squinting at it.
	lookDown() {
		this.eyes.forEach((eye) => eye.aimAt(eye.cx, eye.cy + eye.R * LOOK_UP_HEIGHT))
	}

	blink() {
		this.eyes.forEach((eye) => eye.blink())
	}

	update(dt) {
		this.eyes.forEach((eye) => eye.update(dt))
	}

	destroy() {
		this.eyes.forEach((eye) => eye.destroy())
	}
}

/** Eyes — drives one set of eyes.
 *
 * Owns the cursor position, the frame loop and the blink timer. `start()` builds
 * the set from the markup the first time it runs, so it can be called once the
 * eyes are actually visible (the nav overlay measures nothing while hidden).
 * `stop()` leaves the eyes as they are and costs nothing until started again.
 */
export class Eyes {
	// `pointer: false` ignores the cursor, so the eyes only look where lookAt() says.
	constructor(root, { lookUpOnHover = false, hoverTarget = root, inset = INSET, follow = true, pointer = true } = {}) {
		this.root = root
		this.trackPointer = pointer
		this.elSvg = root.querySelector('svg')
		this.hoverTarget = lookUpOnHover ? hoverTarget : null
		this.inset = inset
		this.follow = follow

		this.set = null
		this.running = false

		// The cursor's last viewport position, and whether the eyes may have moved
		// under it since we last aimed (scroll, resize).
		this.pointer = null
		// A point to look at instead of the cursor, as a function returning a
		// viewport position, so it is re-read after a scroll. See lookAt().
		this.focus = null
		this.stale = false
		this.hovering = false
		this.held = false

		this.bndOnPointerMove = this.onPointerMove.bind(this)
		this.bndOnPointerOut = this.onPointerOut.bind(this)
		this.bndOnClick = this.onClick.bind(this)
		this.bndOnMoved = this.onMoved.bind(this)
		this.bndOnHoverIn = this.onHoverIn.bind(this)
		this.bndOnHoverOut = this.onHoverOut.bind(this)
		this.bndTick = this.tick.bind(this)
	}

	start() {
		if (this.running) return
		this.running = true

		if (!this.set) {
			const groups = Array.from(this.elSvg.querySelectorAll('.eye'))
			this.set = new EyeSet(groups.map((group) => Eye.fromGroup(group, { inset: this.inset, follow: this.follow })))
		}

		if (this.trackPointer) {
			window.addEventListener('pointermove', this.bndOnPointerMove)
			window.addEventListener('pointerout', this.bndOnPointerOut)
		}
		window.addEventListener('click', this.bndOnClick)
		// Capture also catches scrolling inside a container, e.g. an open overlay.
		window.addEventListener('scroll', this.bndOnMoved, { capture: true, passive: true })
		window.addEventListener('resize', this.bndOnMoved, { passive: true })
		this.hoverTarget?.addEventListener('pointerenter', this.bndOnHoverIn)
		this.hoverTarget?.addEventListener('pointerleave', this.bndOnHoverOut)

		this.nextBlink = randomBetween(BLINK_INTERVAL)
		this.lastTime = performance.now()
		this.raf = requestAnimationFrame(this.bndTick)
	}

	stop() {
		if (!this.running) return
		this.running = false

		window.removeEventListener('pointermove', this.bndOnPointerMove)
		window.removeEventListener('pointerout', this.bndOnPointerOut)
		window.removeEventListener('click', this.bndOnClick)
		window.removeEventListener('scroll', this.bndOnMoved, { capture: true })
		window.removeEventListener('resize', this.bndOnMoved)
		this.hoverTarget?.removeEventListener('pointerenter', this.bndOnHoverIn)
		this.hoverTarget?.removeEventListener('pointerleave', this.bndOnHoverOut)

		cancelAnimationFrame(this.raf)
		this.hovering = false

		// Drop any held look, so the next start() opens with the eyes forward.
		if (this.held) {
			this.held = false
			this.set?.lookForward()
		}
	}

	destroy() {
		this.stop()
		this.set?.destroy()
		this.set = null
	}

	blink() {
		this.set?.blink()
	}

	// Look down and stay there until stop() — for the overlay dropping away.
	// Held, so the pointer leaving the closing panel can't snap the eyes back.
	lookDown() {
		this.held = true
		this.set?.lookDown()
	}

	// Look at `getPoint()` (a viewport position) instead of the cursor until
	// lookAt(null). A function rather than a point, so a scroll or resize re-reads
	// it: the thing being looked at moves along with the page. Call it again
	// whenever that thing moves on its own, e.g. a caret while typing.
	lookAt(getPoint) {
		this.focus = getPoint
		if (getPoint || this.pointer) this.aim()
		else this.set?.relax()
	}

	onPointerMove(ev) {
		this.pointer = { x: ev.clientX, y: ev.clientY }
		this.aim()
	}

	// Map the cursor's viewport position into the SVG's own units. Reading the
	// rect here (rather than caching it) is what lets the eyes keep tracking
	// while they themselves move past a cursor that is standing still.
	aim() {
		const point = this.focus ? this.focus() : this.pointer
		if (!point || !this.set || this.hovering || this.held) return

		const rect = this.elSvg.getBoundingClientRect()
		const vb = this.elSvg.viewBox.baseVal
		if (!rect.width || !rect.height) return

		const x = vb.x + ((point.x - rect.left) * vb.width) / rect.width
		const y = vb.y + ((point.y - rect.top) * vb.height) / rect.height

		this.set.setTarget(x, y)
	}

	// `relatedTarget` is null only when the pointer leaves the window.
	onPointerOut(ev) {
		if (ev.relatedTarget !== null) return

		this.pointer = null
		if (!this.focus) this.set.relax()
	}

	// Scroll and resize only raise a flag; the work happens once, in the frame.
	onMoved() {
		this.stale = true
	}

	// While the eyes themselves are hovered they look up instead of tracking.
	onHoverIn() {
		this.hovering = true
		this.set.lookUp()
	}

	onHoverOut() {
		this.hovering = false
		if (this.held) return

		// Back to forward first: an eye with `follow = false` has no cursor to
		// return to, so without this it would stay looking up. For eyes that do
		// follow, aim() overrides it again in the same breath.
		this.set.lookForward()
		this.aim()
	}

	// `click` rather than `pointerdown`, so scrolling on touch doesn't blink.
	onClick() {
		this.set.blink()
		this.nextBlink = randomBetween(BLINK_INTERVAL)
	}

	tick(now) {
		const dt = Math.min((now - this.lastTime) / 1000, 0.05)
		this.lastTime = now

		// Re-aim before this frame's writes, so the layout read stays cheap.
		if (this.stale) {
			this.aim()
			this.stale = false
		}

		this.nextBlink -= dt
		if (this.nextBlink <= 0) {
			this.set.blink()
			this.nextBlink = randomBetween(BLINK_INTERVAL)
		}

		this.set.update(dt)
		this.raf = requestAnimationFrame(this.bndTick)
	}
}
